/**
 * Status Badge Component
 * Reusable badge for displaying invoice status with appropriate colors
 */

import { Badge } from '@/components/ui/badge';
import { InvoiceStatus } from '@/types';

interface StatusBadgeProps {
  status: InvoiceStatus;
  className?: string;
}

const statusConfig: Record<InvoiceStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  [InvoiceStatus.DRAFT]: {
    label: 'Draft',
    variant: 'secondary',
  },
  [InvoiceStatus.ISSUED]: {
    label: 'Issued',
    variant: 'default',
  },
  [InvoiceStatus.PAID]: {
    label: 'Paid',
    variant: 'default',
  },
  [InvoiceStatus.PARTIALLY_PAID]: {
    label: 'Partially Paid',
    variant: 'default',
  },
  [InvoiceStatus.OVERDUE]: {
    label: 'Overdue',
    variant: 'destructive',
  },
  [InvoiceStatus.CANCELLED]: {
    label: 'Cancelled',
    variant: 'outline',
  },
};

const statusColors: Record<InvoiceStatus, string> = {
  [InvoiceStatus.DRAFT]: 'bg-gray-100 text-gray-800 border-gray-300',
  [InvoiceStatus.ISSUED]: 'bg-blue-100 text-blue-800 border-blue-300',
  [InvoiceStatus.PAID]: 'bg-green-100 text-green-800 border-green-300',
  [InvoiceStatus.PARTIALLY_PAID]: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  [InvoiceStatus.OVERDUE]: 'bg-red-100 text-red-800 border-red-300',
  [InvoiceStatus.CANCELLED]: 'bg-gray-100 text-gray-600 border-gray-300',
};

export const StatusBadge = ({ status, className }: StatusBadgeProps) => {
  const config = statusConfig[status];
  const colorClass = statusColors[status];

  return (
    <Badge
      variant={config.variant}
      className={`${colorClass} font-medium ${className || ''}`}
    >
      {config.label}
    </Badge>
  );
};
